import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { canManage } from '@/lib/auth';
import { useAuth } from '@/lib/auth';
import ClinicsTab from '@/components/admin/ClinicsTab';
import EmployeesTab from '@/components/admin/EmployeesTab';
import PatientsTab from '@/components/admin/PatientsTab';
import RoomsTab from '@/components/admin/RoomsTab';
import AppointmentTypesTab from '@/components/admin/AppointmentTypesTab';
import HolidaysTab from '@/components/admin/HolidaysTab';
import UsersTab from '@/components/admin/UsersTab';
import SettingsTab from '@/components/admin/SettingsTab';
import { BrandsTab, SourcesTab, SubsourcesTab, ConsultantsTab, ItemsTab } from '@/components/admin/MasterTabs';
import ClustersTab from '@/components/admin/ClustersTab';
import InventoryTab from '@/components/admin/InventoryTab';
import AgentsTab from '@/components/admin/AgentsTab';
import AudiologistLinksTab from '@/components/admin/AudiologistLinksTab';
import { ADMIN } from '@/constants/testIds';
import {
  Building2, Users, UserRound, Home, ListChecks, CalendarX,
  UserCog, Settings2, Tag, GitBranch, Stethoscope, Package, Layers, Boxes, Bot,
} from 'lucide-react';

const TABS = [
  { value: 'clinics', label: 'Clinics', icon: Building2, C: ClinicsTab },
  { value: 'clusters', label: 'Clusters', icon: Layers, C: ClustersTab },
  { value: 'employees', label: 'Staff', icon: Users, C: EmployeesTab },
  { value: 'patients', label: 'Patients', icon: UserRound, C: PatientsTab },
  { value: 'rooms', label: 'Resources', icon: Home, C: RoomsTab },
  { value: 'appointment-types', label: 'Appointment Types', icon: ListChecks, C: AppointmentTypesTab },
  { value: 'consultants', label: 'Consultants', icon: Stethoscope, C: ConsultantsTab },
  { value: 'sources', label: 'Sources', icon: Tag, C: SourcesTab },
  { value: 'subsources', label: 'Sub-sources', icon: GitBranch, C: SubsourcesTab },
  { value: 'brands', label: 'Brands', icon: Tag, C: BrandsTab },
  { value: 'items', label: 'Items', icon: Package, C: ItemsTab },
  { value: 'inventory', label: 'Inventory', icon: Boxes, C: InventoryTab },
  { value: 'holidays', label: 'Holidays', icon: CalendarX, C: HolidaysTab },
  { value: 'users', label: 'Users & Roles', icon: UserCog, C: UsersTab },
  { value: 'agents', label: 'AI Agents', icon: Bot, C: AgentsTab },
  { value: 'audiologist-agents', label: 'Audiologist Agents', icon: UserCog, C: AudiologistLinksTab },
  { value: 'settings', label: 'Settings & Sync', icon: Settings2, C: SettingsTab },
];

export default function AdminPage() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const active = tab || 'clinics';

  if (!canManage(user?.role)) {
    return (
      <div className="p-8 text-stone-600">
        You don't have access to the admin console.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto hc-scrollbar" data-testid={ADMIN.page}>
      <div className="max-w-7xl mx-auto p-4">
        <div className="mb-4">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Configuration</div>
          <h1 className="font-heading text-2xl font-bold text-stone-900">Admin Console</h1>
          <p className="text-sm text-stone-500 mt-0.5">Configure clinics, staff, resources, appointment types, holidays and users.</p>
        </div>
        <Tabs value={active} onValueChange={(v) => navigate(`/admin/${v}`)} className="w-full">
          <TabsList className="grid grid-cols-4 lg:grid-cols-7 xl:grid-cols-13 h-auto p-1 bg-stone-100 mb-4 gap-1">
            {TABS.map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                data-testid={ADMIN.tab(t.value)}
                className="text-[10px] h-8 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <t.icon className="w-3 h-3 mr-1" />
                <span className="hidden xl:inline">{t.label}</span>
                <span className="xl:hidden">{t.label.split(' ')[0]}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map((t) => (
            <TabsContent key={t.value} value={t.value} className="mt-0">
              <t.C />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
